import { compareText, routes, type TaskStatusFilter } from '@pmai/contracts';
import { Alert, Button, EmptyState, FilterSelect } from '@pmai/ui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TaskDialog } from '../../entities/tasks/task-dialog';
import { TaskStatusSelect } from '../../entities/tasks/task-filters';
import { TaskSummaryLine, TasksTable } from '../../entities/tasks/tasks-table';
import { useApiQuery } from '../../shared/api/hooks';
import { Can } from '../../shared/auth/session';
import { CardSection } from '../../shared/card-section';
import { useCustomerProjects } from './customer-parts';

/**
 * "Uzdevumi" on the customer card (FR-CUST-013…016): all tasks of the customer's projects,
 * filtered by status (default "Nav pabeigti"), project and responsible person.
 */
export function CustomerTasksSection({ customerId }: { customerId: string }) {
  const { t } = useTranslation();
  const scope = { kind: 'customer', id: customerId } as const;
  const [status, setStatus] = useState<TaskStatusFilter>('open');
  const [projectId, setProjectId] = useState<string | null>(null);
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [noProjects, setNoProjects] = useState(false);
  const projects = useCustomerProjects(customerId);
  // Every task of the customer — the responsible people that occur in them are the filter's choices.
  const all = useApiQuery(routes.tasks.list, { query: { customerId, status: 'all', order: 'board' } });
  const tasks = useApiQuery(
    routes.tasks.list,
    {
      query: {
        customerId,
        status,
        projectId: projectId ?? undefined,
        assigneeId: assigneeId ?? undefined,
        order: 'board',
      },
    },
    { placeholderData: (previous) => previous },
  );
  const assignees = useMemo(() => {
    const seen = new Map<string, string>();
    for (const task of all.data?.rows ?? [])
      if (task.assigneeId && task.assigneeName) seen.set(task.assigneeId, task.assigneeName);
    return [...seen]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => compareText(a.label, b.label));
  }, [all.data]);

  return (
    <CardSection
      id="customer.tasks"
      title={t('customers.sections.tasks')}
      actions={
        <Can perm="tasks.edit">
          <Button
            size="sm"
            icon="plus"
            onClick={() => (projects.data?.rows.length ? setAdding(true) : setNoProjects(true))}
          >
            {t('tasks.add')}
          </Button>
        </Can>
      }
    >
      <div className="flex flex-col gap-4">
        {noProjects && <Alert tone="warning">{t('tasks.noCustomerProjects')}</Alert>}
        <div className="grid gap-3 sm:grid-cols-3">
          <TaskStatusSelect value={status} onChange={setStatus} />
          <FilterSelect
            label={t('tasks.fields.project')}
            allLabel={t('tasks.allProjects')}
            options={(projects.data?.rows ?? []).map((p) => ({ value: p.id, label: p.name }))}
            value={projectId}
            onChange={setProjectId}
          />
          <FilterSelect
            label={t('tasks.fields.assignee')}
            allLabel={t('tasks.allAssignees')}
            options={assignees}
            value={assigneeId}
            onChange={setAssigneeId}
          />
        </div>
        {tasks.data && <TaskSummaryLine summary={tasks.data.summary} />}
        <TasksTable
          label={t('customers.sections.tasks')}
          rows={tasks.data?.rows}
          loading={tasks.isFetching}
          columns={['check', 'project', 'title', 'assignee', 'plan', 'fact', 'deadline', 'actions']}
          scope={scope}
          totals={tasks.data?.summary}
          empty={<EmptyState icon="list-checks" title={t('tasks.emptyFiltered')} />}
        />
      </div>
      <TaskDialog open={adding} task={null} scope={scope} onClose={() => setAdding(false)} />
    </CardSection>
  );
}
